/**
 * Related Records Tab
 *
 * PE-769: Allows users to link any record to any other record in the CRM.
 * Displays current relationships and provides a lookup picker to add new ones.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link2,
  Plus,
  X,
  Search,
  Users,
  User,
  UserPlus,
  DollarSign,
  Briefcase,
  CheckSquare,
  Calendar,
  ExternalLink,
  ChevronDown,
  Trash2,
  Clock
} from 'lucide-react';
import {
  getRelatedRecords,
  addRelatedRecord,
  removeRelatedRecord,
  relationshipExists,
  getRecordsForLookup,
  getRecordById,
  getRecordDisplayName,
  getObjectTypeConfig,
  RELATABLE_OBJECTS,
  type RelatedRecord,
  type ObjectTypeConfig
} from '../../services/relatedRecordsService';
import { useToast } from '../../contexts/ToastContext';

interface RelatedRecordsTabProps {
  /** Current record ID */
  recordId: string;
  /** Current object type (e.g., 'Task__c', 'Client__c') */
  objectType: string;
  /** Record name for display */
  recordName?: string;
  /** Callback when navigating to a related record */
  onNavigateToRecord?: (recordId: string, objectType: string) => void;
}

// Icon mapping for object types
const objectIcons: Record<string, React.ElementType> = {
  'Client__c': Users,
  'Contact__c': User,
  'Lead__c': UserPlus,
  'Opportunity__c': DollarSign,
  'Case__c': Briefcase,
  'Task__c': CheckSquare,
  'Event__c': Calendar
};

export const RelatedRecordsTab: React.FC<RelatedRecordsTabProps> = ({
  recordId,
  objectType,
  recordName,
  onNavigateToRecord
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  // State
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedObjectType, setSelectedObjectType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isObjectDropdownOpen, setIsObjectDropdownOpen] = useState(false);

  // Fetch current related records
  const { data: relatedRecords = [], isLoading, refetch } = useQuery({
    queryKey: ['relatedRecords', recordId, objectType],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      return getRelatedRecords(recordId, objectType);
    }
  });

  // Fetch lookup records when searching
  const { data: lookupRecords = [] } = useQuery({
    queryKey: ['lookupRecords', selectedObjectType, searchTerm],
    queryFn: async () => {
      if (!selectedObjectType) return [];
      await new Promise(resolve => setTimeout(resolve, 100));
      return getRecordsForLookup(selectedObjectType, searchTerm);
    },
    enabled: !!selectedObjectType && isAddingNew
  });

  // Get enriched related records with display info
  const enrichedRecords = useMemo(() => {
    return relatedRecords.map(rel => {
      // Determine which side of the relationship this record is on
      const isSource = rel.recordId === recordId && rel.objectType === objectType;
      const relatedObjType = isSource ? rel.relatedObjectType : rel.objectType;
      const relatedId = isSource ? rel.relatedRecordId : rel.recordId;

      const record = getRecordById(relatedObjType, relatedId);
      const config = getObjectTypeConfig(relatedObjType);

      return {
        ...rel,
        relatedObjType,
        relatedId,
        displayName: record ? getRecordDisplayName(relatedObjType, record) : 'Unknown Record',
        objectLabel: config?.label || relatedObjType,
        record
      };
    });
  }, [relatedRecords, recordId, objectType]);

  // Available object types (exclude current object type)
  const availableObjectTypes = useMemo(() => {
    return RELATABLE_OBJECTS.filter(obj => obj.apiName !== objectType);
  }, [objectType]);

  // Handle adding a new relationship
  const handleAddRelationship = useCallback((relatedId: string) => {
    if (!selectedObjectType) return;

    // Check if relationship already exists
    if (relationshipExists(recordId, objectType, relatedId, selectedObjectType)) {
      showError('Already Related', 'This record is already linked.');
      return;
    }

    try {
      addRelatedRecord(recordId, objectType, relatedId, selectedObjectType);
      success('Record Linked', 'The record has been linked successfully.');

      // Reset state
      setIsAddingNew(false);
      setSelectedObjectType('');
      setSearchTerm('');

      // Refresh the list
      refetch();
      queryClient.invalidateQueries({ queryKey: ['relatedRecords'] });
    } catch (err) {
      showError('Error', 'Failed to link record. Please try again.');
    }
  }, [recordId, objectType, selectedObjectType, refetch, queryClient, success, showError]);

  // Handle removing a relationship
  const handleRemoveRelationship = useCallback((relationId: string) => {
    try {
      removeRelatedRecord(relationId);
      success('Link Removed', 'The relationship has been removed.');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['relatedRecords'] });
    } catch (err) {
      showError('Error', 'Failed to remove link. Please try again.');
    }
  }, [refetch, queryClient, success, showError]);

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-9 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Related Records ({enrichedRecords.length})
        </h3>
        {!isAddingNew && (
          <button
            onClick={() => setIsAddingNew(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Related Record
          </button>
        )}
      </div>

      {/* Add New Section */}
      {isAddingNew && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-blue-900 dark:text-blue-100">
              Link a Related Record
            </h4>
            <button
              onClick={() => {
                setIsAddingNew(false);
                setSelectedObjectType('');
                setSearchTerm('');
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Object Type Selector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Object Type
            </label>
            <div className="relative">
              <button
                onClick={() => setIsObjectDropdownOpen(!isObjectDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-left"
              >
                <span className={selectedObjectType ? 'text-gray-900 dark:text-white' : 'text-gray-500'}>
                  {selectedObjectType
                    ? getObjectTypeConfig(selectedObjectType)?.label
                    : 'Choose an object type...'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {isObjectDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {availableObjectTypes.map(obj => {
                    const Icon = objectIcons[obj.apiName] || Link2;
                    return (
                      <button
                        key={obj.apiName}
                        onClick={() => {
                          setSelectedObjectType(obj.apiName);
                          setIsObjectDropdownOpen(false);
                          setSearchTerm('');
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                      >
                        <Icon className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 dark:text-white">{obj.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Record Search */}
          {selectedObjectType && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Search {getObjectTypeConfig(selectedObjectType)?.pluralLabel}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Search ${getObjectTypeConfig(selectedObjectType)?.pluralLabel?.toLowerCase()}...`}
                  className="w-full pl-9 pr-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>

              {/* Search Results */}
              {lookupRecords.length > 0 ? (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                  {lookupRecords.map(record => {
                    const displayName = getRecordDisplayName(selectedObjectType, record);
                    const alreadyLinked = relationshipExists(recordId, objectType, record.id, selectedObjectType);

                    return (
                      <button
                        key={record.id}
                        onClick={() => !alreadyLinked && handleAddRelationship(record.id)}
                        disabled={alreadyLinked}
                        className={`w-full flex items-center justify-between px-3 py-2 text-left ${
                          alreadyLinked
                            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div>
                          <span className={`text-sm ${alreadyLinked ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                            {displayName}
                          </span>
                          {record.email && (
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                              {record.email}
                            </span>
                          )}
                        </div>
                        {alreadyLinked ? (
                          <span className="text-xs text-gray-400">Already linked</span>
                        ) : (
                          <Plus className="w-4 h-4 text-blue-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : searchTerm ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                  No {getObjectTypeConfig(selectedObjectType)?.pluralLabel?.toLowerCase()} found matching "{searchTerm}"
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                  Type to search for {getObjectTypeConfig(selectedObjectType)?.pluralLabel?.toLowerCase()}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Related Records List */}
      {enrichedRecords.length === 0 && !isAddingNew ? (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="text-center">
            <Link2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Related Records
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Link this record to other clients, contacts, cases, or opportunities.
            </p>
            <button
              onClick={() => setIsAddingNew(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Related Record
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {enrichedRecords.map(rel => {
            const Icon = objectIcons[rel.relatedObjType] || Link2;

            return (
              <div
                key={rel.id}
                className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {rel.displayName}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                          {rel.objectLabel}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Linked {formatRelativeTime(rel.createdAt)}
                        </span>
                        <span>by {rel.createdByName}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {onNavigateToRecord && (
                      <button
                        onClick={() => onNavigateToRecord(rel.relatedId, rel.relatedObjType)}
                        className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title="View Record"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveRelationship(rel.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Remove Link"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RelatedRecordsTab;
